import {
  doc,
  getDocs,
  updateDoc,
  collectionGroup,
  query,
  where,
  and,
  or,
  orderBy,
  onSnapshot,
  writeBatch,
} from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import { handleAuthError } from './authRecoveryService';
import type { Notification } from '../types';
import type { UserRole } from '../types/settings';

// ============================================================================
// NOTIFICATION QUERIES
// ============================================================================

/**
 * Subscribe to delivered notifications (real-time updates)
 * Uses collection group queries with RBAC:
 * - Admins see all org notifications
 * - Standard users see notifications for items they created or own
 */
export function subscribeNotifications(
  userId: string,
  orgId: string,
  role: UserRole,
  callback: (notifications: Notification[]) => void
): () => void {
  const db = getFirebaseDb();

  let notifQuery;

  if (role === 'admin' || role === 'superAdmin') {
    // Admins see all org notifications
    notifQuery = query(
      collectionGroup(db, 'notifications'),
      where('organizationId', '==', orgId),
      where('status', '==', 'delivered'),
      orderBy('notificationDateTimeUTC', 'desc')
    );
  } else {
    // Standard users see notifications for items they created or own
    notifQuery = query(
      collectionGroup(db, 'notifications'),
      and(
        where('organizationId', '==', orgId),
        where('status', '==', 'delivered'),
        or(
          where('createdBy', '==', userId),
          where('owner', '==', userId)
        )
      )
    );
  }

  return onSnapshot(notifQuery, (snapshot) => {
    const notifications = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as Notification[];
    notifications.sort((a, b) => {
      const aTime = a.notificationDateTimeUTC?.toMillis() || 0;
      const bTime = b.notificationDateTimeUTC?.toMillis() || 0;
      return bTime - aTime;
    });
    callback(notifications);
  }, async (error) => {
    console.error('[NotificationService] Error subscribing to notifications:', error);
    await handleAuthError(error);
  });
}

/**
 * Get all delivered notifications
 */
export async function getDeliveredNotifications(
  userId: string,
  orgId: string,
  role: UserRole
): Promise<Notification[]> {
  const db = getFirebaseDb();

  let notifQuery;

  if (role === 'admin' || role === 'superAdmin') {
    notifQuery = query(
      collectionGroup(db, 'notifications'),
      where('organizationId', '==', orgId),
      where('status', '==', 'delivered'),
      orderBy('notificationDateTimeUTC', 'desc')
    );
  } else {
    notifQuery = query(
      collectionGroup(db, 'notifications'),
      and(
        where('organizationId', '==', orgId),
        where('status', '==', 'delivered'),
        or(
          where('createdBy', '==', userId),
          where('owner', '==', userId)
        )
      )
    );
  }

  const snapshot = await getDocs(notifQuery);

  const notifications = snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as Notification[];
  notifications.sort((a, b) => {
    const aTime = a.notificationDateTimeUTC?.toMillis() || 0;
    const bTime = b.notificationDateTimeUTC?.toMillis() || 0;
    return bTime - aTime;
  });
  return notifications;
}

// ============================================================================
// NOTIFICATION ACTIONS
// ============================================================================

/**
 * Clear a single notification (set status to 'clear')
 * Uses recipientUserId to construct the correct document path
 */
export async function clearNotification(
  notificationId: string,
  recipientUserId: string
): Promise<void> {
  const db = getFirebaseDb();
  await updateDoc(doc(db, 'users', recipientUserId, 'notifications', notificationId), {
    status: 'clear',
  });
}

/**
 * Clear specific notifications by their recipientUserId + ID pairs
 * Handles notifications across multiple users' subcollections
 */
export async function clearNotificationsByIds(
  notifications: Array<{ id: string; recipientUserId: string }>
): Promise<void> {
  if (notifications.length === 0) return;

  const db = getFirebaseDb();

  for (let i = 0; i < notifications.length; i += 500) {
    const batch = writeBatch(db);
    notifications.slice(i, i + 500).forEach(({ id, recipientUserId }) => {
      batch.update(doc(db, 'users', recipientUserId, 'notifications', id), { status: 'clear' });
    });
    await batch.commit();
  }
}

/**
 * Permanently delete all notifications for a specific item (used when item is deleted)
 * Uses collection group to find notifications across all users' subcollections
 */
export async function deleteNotificationsByItemId(
  itemId: string
): Promise<void> {
  const db = getFirebaseDb();
  // Use collection group to find all notifications for this item across all users
  const q = query(
    collectionGroup(db, 'notifications'),
    where('itemId', '==', itemId)
  );
  const snapshot = await getDocs(q);

  if (snapshot.empty) return;

  const docs = snapshot.docs;
  for (let i = 0; i < docs.length; i += 500) {
    const batch = writeBatch(db);
    docs.slice(i, i + 500).forEach(docSnap => batch.delete(docSnap.ref));
    await batch.commit();
  }
}

/**
 * Permanently delete all notifications for a specific sub-item (used when sub-item is deleted)
 * Uses collection group to find notifications across all users' subcollections
 */
export async function deleteNotificationsBySubItemId(
  itemId: string,
  subItemId: string
): Promise<void> {
  const db = getFirebaseDb();
  const q = query(
    collectionGroup(db, 'notifications'),
    where('itemId', '==', itemId),
    where('subItemId', '==', subItemId)
  );
  const snapshot = await getDocs(q);

  if (snapshot.empty) return;

  const docs = snapshot.docs;
  for (let i = 0; i < docs.length; i += 500) {
    const batch = writeBatch(db);
    docs.slice(i, i + 500).forEach(docSnap => batch.delete(docSnap.ref));
    await batch.commit();
  }
}

/**
 * Get delivered notification count (for badge)
 */
export async function getNotificationCount(
  userId: string,
  orgId: string,
  role: UserRole
): Promise<number> {
  const notifications = await getDeliveredNotifications(userId, orgId, role);
  return notifications.length;
}
